// Brand-aware structured-data (JSON-LD) builders for GEO.
// Injected app-wide by App.js (and CateringApp.js) via useJsonLd so every route
// carries an Organization / FoodService node + a WebSite node — the primary lever
// for AI-search visibility (the weakest GEO dimension across the brand portfolio).
import brand from '../config/brand';

// Cross-link the brand family so search engines disambiguate each entity and
// associate sibling sites. Each entry lists authoritative URLs for that brand
// (no self-reference).
const SAME_AS = {
  mgv: [
    'https://openbadge.me',
    'https://chefkunming.com',
    'https://visit-kunming.com',
    'https://omniversail.com',
    'https://extramerriman.com',
  ],
  openbadge: [
    'https://www.mgv.gallery',
    'https://chefkunming.com',
    'https://visit-kunming.com',
    'https://omniversail.com',
  ],
  chefkunming: [
    'https://www.mgv.gallery',
    'https://visit-kunming.com',
    'https://openbadge.me',
  ],
  visitkunming: [
    'https://www.mgv.gallery',
    'https://chefkunming.com',
    'https://openbadge.me',
  ],
};

const BRAND_GEO = {
  mgv: {
    type: 'Organization',
    name: 'MGV Gallery',
    alternateName: 'Mobile Gallery Venture',
    description:
      'MGV Gallery delivers curated hospitality, gallery, academic and creative services — excellence in service and unforgettable experiences across Kunming and beyond.',
    logo: 'https://www.mgv.gallery/logo192.png',
  },
  openbadge: {
    type: 'Organization',
    name: 'OpenBadge',
    alternateName: 'MGV Gallery',
    description:
      'OpenBadge — verifiable credentials and a dynamic human-AI competency portfolio by MGV Gallery.',
    logo: 'https://openbadge.me/openbadge-badge.svg?v=11',
  },
  chefkunming: {
    type: 'FoodService',
    name: 'Chef Kunming',
    alternateName: 'Chef Kunming Private Dining',
    description:
      'Private chefs and curated catering in Kunming — in-home Western & fusion dining, events, cocktail parties and long-stay culinary services for Kunming expats and international families.',
    logo: 'https://chefkunming.com/logo192.png',
    areaServed: 'Kunming, Yunnan, China',
    servesCuisine: ['Western', 'Fusion', 'Chinese', 'Vegetarian'],
    priceRange: '$$',
    acceptsReservations: true,
    currenciesAccepted: 'CNY',
  },
  visitkunming: {
    type: 'TravelAgency',
    name: 'Visit Kunming',
    description:
      'Discover Kunming — inbound travel, certified stays, curated itineraries, hiking routes and long-stay services.',
    logo: 'https://visit-kunming.com/logo192.png',
  },
};

export function getOrganizationJsonLd(b = brand) {
  const g = BRAND_GEO[b.key] || BRAND_GEO.mgv;
  const origin = b.origin && b.origin.startsWith('http') ? b.origin : 'https://www.mgv.gallery';
  const node = {
    '@context': 'https://schema.org',
    '@type': g.type,
    '@id': `${origin}/#organization`,
    name: g.name,
    url: origin,
    logo: g.logo,
    description: g.description,
  };
  if (g.alternateName) node.alternateName = g.alternateName;
  if (g.areaServed) node.areaServed = g.areaServed;
  if (g.servesCuisine) node.servesCuisine = g.servesCuisine;
  if (g.priceRange) node.priceRange = g.priceRange;
  if (g.acceptsReservations !== undefined) node.acceptsReservations = g.acceptsReservations;
  if (g.currenciesAccepted) node.currenciesAccepted = g.currenciesAccepted;
  if (g.type === 'FoodService' || g.type === 'TravelAgency') {
    node.address = {
      '@type': 'PostalAddress',
      addressLocality: 'Kunming',
      addressRegion: 'Yunnan',
      addressCountry: 'CN',
    };
  }
  const sameAs = SAME_AS[b.key];
  if (sameAs && sameAs.length) node.sameAs = sameAs;
  return node;
}

export function getWebSiteJsonLd(b = brand) {
  const g = BRAND_GEO[b.key] || BRAND_GEO.mgv;
  const origin = b.origin && b.origin.startsWith('http') ? b.origin : 'https://www.mgv.gallery';
  const node = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${origin}/#website`,
    url: origin,
    name: g.name,
    description: g.description,
    inLanguage: ['en', 'zh'],
    publisher: { '@id': `${origin}/#organization` },
  };
  // Sitelinks searchbox — only where a working /search route exists.
  if (b.key === 'mgv') {
    node.potentialAction = {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${origin}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    };
  }
  return node;
}
